/**
 * Centralised Supabase env resolution.
 *
 * Supports both the new publishable key (sb_publishable_...) and the legacy
 * anon key — preferring the publishable key if both are set. Anon will be
 * deprecated; keeping the fallback makes the cut-over painless.
 *
 * Throws at module load time if required vars are missing — fail fast beats
 * failing deep inside a request handler.
 *
 * Important: every NEXT_PUBLIC_* read must use literal property access
 * (`process.env.NEXT_PUBLIC_FOO`) so Next/Turbopack can statically inline
 * the value into client + SSR chunks at build time. Dynamic access
 * (`process.env[name]`) is NOT inlined and will evaluate to `undefined`
 * inside bundled chunks, even when the Node dev server has loaded
 * `.env.local` correctly. The `ensure` helper takes the already-resolved
 * value as a parameter so the inlining happens at each call site.
 */

function ensure(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `[supabase/env] Missing required environment variable: ${name}`,
    )
  }
  return value
}

export const SUPABASE_URL = ensure(
  "NEXT_PUBLIC_SUPABASE_URL",
  process.env.NEXT_PUBLIC_SUPABASE_URL,
)

export const SUPABASE_PUBLISHABLE_KEY =
  process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ??
  ensure(
    "NEXT_PUBLIC_SUPABASE_ANON_KEY",
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
  )

/**
 * Service-role key. Read lazily (via function) so that importing this module
 * from a client bundle never triggers the check — only server callers that
 * actually need it will force the read. Server-only env vars (no
 * NEXT_PUBLIC_ prefix) are always available on `process.env` in Node, so
 * literal access here is also correct.
 */
export function getServiceRoleKey(): string {
  return ensure("SUPABASE_SERVICE_ROLE_KEY", process.env.SUPABASE_SERVICE_ROLE_KEY)
}
